import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "react-hot-toast";
import { RiDownloadLine, RiBarChart2Line, RiArrowLeftSLine, RiArrowRightSLine } from "@remixicon/react";
import { streamChat } from "../../services/agentStream.ts";
import { updateMessageFeedback } from "../../services/ApiService.ts";
import { useExportDownload } from "./useExportDownload.ts";
import { DataChatMessage, DataSource } from "../../types.ts";
import ChartRenderer from "./ChartRenderer.tsx";
import { SparklesIcon } from "./Icons.tsx";

// ── Types ────────────────────────────────────────────────────────────────────

type ToolActivity = { kind: "tool"; id: string; name: string; status: "running" | "done"; durationMs: number | null; sql?: string };
type ThinkingActivity = { kind: "thinking"; id: string };
type Activity = ToolActivity | ThinkingActivity;

type UserMsg = { role: "user"; id: string; content: string };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
type AssistantMsg = { role: "assistant"; id: string; dbId?: string; content: string; charts: any[]; activities: Activity[] };
type Msg = UserMsg | AssistantMsg;

const TOOL_LABELS: Record<string, string> = {
  execute_sql: "SQL uitvoeren",
  generate_chart_component: "Grafiek genereren",
};

const VIZ_PANEL_WIDTH_STORAGE_KEY = "datastudio-viz-panel-width";
const DEFAULT_VIZ_PANEL_WIDTH = 420;
const MIN_VIZ_PANEL_WIDTH = 320;
const MAX_VIZ_PANEL_WIDTH = 720;
const COLLAPSED_VIZ_RAIL_WIDTH = 48;

// ── Persistence helpers ──────────────────────────────────────────────────────

function getFallbackMsgId(message: DataChatMessage, index: number): string {
  if (message.id) return message.id;
  if (message.dbId) return `db-${message.dbId}`;
  if (message.timestamp !== undefined) return `${message.role}-${message.type}-${message.timestamp}-${index}`;
  return `${message.role}-${message.type}-${index}`;
}

function fromInitialMessages(messages: DataChatMessage[]): Msg[] {
  const result: Msg[] = [];
  for (const [index, m] of messages.entries()) {
    const messageId = getFallbackMsgId(m, index);
    if (m.role === "user") {
      result.push({ role: "user", id: messageId, content: String(m.content) });
    } else if (m.type === "text" && typeof m.content === "string") {
      const prev = result[result.length - 1];
      if (prev?.role === "assistant") {
        prev.content += (prev.content ? "\n\n" : "") + m.content;
        if (m.dbId) prev.dbId = m.dbId;
      } else {
        result.push({ role: "assistant", id: messageId, dbId: m.dbId, content: m.content, charts: [], activities: [] });
      }
    } else if (m.type === "component" && typeof m.content === "object") {
      const prev = result[result.length - 1];
      if (prev?.role === "assistant") {
        prev.charts.push(m.content);
      }
    }
  }
  return result;
}

function toDataChatMessages(msgs: Msg[]): DataChatMessage[] {
  const out: DataChatMessage[] = [];
  for (const m of msgs) {
    const timestamp = Date.now();
    if (m.role === "user") {
      out.push({ id: m.id, role: "user", type: "text", content: m.content, timestamp });
    } else {
      if (m.content) out.push({ id: m.id, dbId: m.dbId, role: "assistant", type: "text", content: m.content, timestamp });
      for (const [chartIndex, chart] of m.charts.entries()) {
        out.push({ id: `${m.id}-chart-${chartIndex}`, role: "assistant", type: "component", content: chart, timestamp });
      }
    }
  }
  return out;
}

// ── Component ────────────────────────────────────────────────────────────────

interface DataChatProps {
  dataSources: DataSource[];
  awaitingData?: boolean;
  administrationCode: number;
  onGeneratedDataChange?: (data: Record<string, Record<string, unknown>[]>) => void;
  initialMessages?: DataChatMessage[];
  initialSessionId?: string;
  onMessagesChange?: (messages: DataChatMessage[], sessionId?: string) => void;
}

const DataChat = ({ dataSources, awaitingData = false, administrationCode, onGeneratedDataChange, initialMessages, initialSessionId, onMessagesChange }: DataChatProps) => {
  const runExport = useExportDownload();
  const [msgs, setMsgs] = useState<Msg[]>(() => fromInitialMessages(initialMessages || []));
  const [streaming, setStreaming] = useState<AssistantMsg | null>(null);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(initialSessionId);
  const [vizPanelOpen, setVizPanelOpen] = useState(false);
  const [vizPanelWidth, setVizPanelWidth] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_VIZ_PANEL_WIDTH;
    const saved = window.localStorage.getItem(VIZ_PANEL_WIDTH_STORAGE_KEY);
    const parsed = saved ? Number.parseInt(saved, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : DEFAULT_VIZ_PANEL_WIDTH;
  });
  const [isResizingVizPanel, setIsResizingVizPanel] = useState(false);
  const [selectedChartIdx, setSelectedChartIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const msgsRef = useRef<Msg[]>(msgs);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const userScrollingRef = useRef(false);
  const userScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);
  const rafRef = useRef<number | null>(null);
  const resizeStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const pendingRequestRef = useRef<{ previousMsgs: Msg[]; userMsg: UserMsg; sessionId?: string } | null>(null);

  const clampVizPanelWidth = useCallback((nextWidth: number) => {
    const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 0;
    const maxFromContainer = containerWidth > 0
      ? Math.max(MIN_VIZ_PANEL_WIDTH, Math.min(MAX_VIZ_PANEL_WIDTH, Math.floor(containerWidth * 0.65)))
      : MAX_VIZ_PANEL_WIDTH;
    return Math.min(maxFromContainer, Math.max(MIN_VIZ_PANEL_WIDTH, nextWidth));
  }, []);

  const scrollToBottom = () => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 80) nearBottomRef.current = true;
    else if (userScrollingRef.current) nearBottomRef.current = false;
  };

  const markUserScrolling = () => {
    userScrollingRef.current = true;
    if (userScrollTimerRef.current) clearTimeout(userScrollTimerRef.current);
    userScrollTimerRef.current = setTimeout(() => { userScrollingRef.current = false; }, 200);
  };

  const holdUserScrolling = () => {
    if (userScrollTimerRef.current) clearTimeout(userScrollTimerRef.current);
    userScrollingRef.current = true;
    const release = () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
      markUserScrolling();
    };
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
  };

  useEffect(() => { msgsRef.current = msgs; }, [msgs]);
  useEffect(() => {
    if (nearBottomRef.current) scrollToBottom();
  }, [msgs, streaming]);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (nearBottomRef.current) scrollToBottom();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIZ_PANEL_WIDTH_STORAGE_KEY, String(vizPanelWidth));
  }, [vizPanelWidth]);
  useEffect(() => {
    setSessionId(initialSessionId);
  }, [initialSessionId]);
  useEffect(() => () => {
    cancelRef.current?.();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (userScrollTimerRef.current) clearTimeout(userScrollTimerRef.current);
  }, []);
  useEffect(() => {
    const handleWindowResize = () => {
      setVizPanelWidth((current) => clampVizPanelWidth(current));
    };

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [clampVizPanelWidth]);
  useEffect(() => {
    setVizPanelWidth((current) => clampVizPanelWidth(current));
  }, [clampVizPanelWidth]);
  useEffect(() => {
    if (!isResizingVizPanel) return;

    const handleMouseMove = (event: MouseEvent) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;
      const nextWidth = resizeState.startWidth + (resizeState.startX - event.clientX);
      setVizPanelWidth(clampVizPanelWidth(nextWidth));
    };

    const handleMouseUp = () => {
      resizeStateRef.current = null;
      setIsResizingVizPanel(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [clampVizPanelWidth, isResizingVizPanel]);

  const handleSend = useCallback(async () => {
    if (!inputMessage.trim() || loading || dataSources.length === 0) return;

    const trimmedMessage = inputMessage.trim();
    const previousMsgs = msgsRef.current;
    const userMsg: UserMsg = { role: "user", id: `u-${Date.now()}`, content: trimmedMessage };
    pendingRequestRef.current = { previousMsgs, userMsg, sessionId };
    setMsgs(prev => [...prev, userMsg]);
    nearBottomRef.current = true;
    setTimeout(scrollToBottom, 0);
    const history = toDataChatMessages(previousMsgs);
    setInputMessage("");
    setLoading(true);
    const assistantId = `a-${Date.now()}`;
    const currentMsg: AssistantMsg = {
      role: "assistant",
      id: assistantId,
      content: "",
      charts: [],
      activities: [{ kind: "thinking", id: `thinking-${assistantId}-0` }],
    };
    setStreaming({ ...currentMsg });

    const toolStartTimes: Record<string, number> = {};

    const cancel = streamChat(
      {
        message: trimmedMessage,
        // The backend accepts exactly one reference; a saved source keeps its task_id in state.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- server-only fields are stripped from the request body
        data_sources: dataSources.map(({ row_count: _rowCount, state: _state, error_message: _errorMessage, task_id, ...source }) =>
          source.library_item_id ? source : { ...source, task_id },
        ),
        session_id: sessionId,
        history,
        administration_code: administrationCode,
      },
      (event) => {
        const findTool = (toolCallId: string) =>
          currentMsg.activities.find(
            (activity): activity is ToolActivity => activity.kind === "tool" && activity.id === toolCallId
          );

        const removeTrailingThinking = () => {
          const last = currentMsg.activities[currentMsg.activities.length - 1];
          if (last?.kind === "thinking") {
            currentMsg.activities = currentMsg.activities.slice(0, -1);
          }
        };

        if (event.type === "tool_call") {
          toolStartTimes[event.tool_call_id] = Date.now();
          const toolActivity: ToolActivity = {
            kind: "tool",
            id: event.tool_call_id,
            name: event.tool_name,
            status: "running",
            durationMs: null,
          };
          const last = currentMsg.activities[currentMsg.activities.length - 1];
          if (last?.kind === "thinking") {
            currentMsg.activities = [...currentMsg.activities.slice(0, -1), toolActivity];
          } else {
            currentMsg.activities = [...currentMsg.activities, toolActivity];
          }
          setStreaming({ ...currentMsg });
        } else if (event.type === "tool_result") {
          const startedAt = toolStartTimes[event.tool_call_id];
          const durationMs = startedAt ? Date.now() - startedAt : null;
          const tc = findTool(event.tool_call_id);
          if (tc) { tc.status = "done"; tc.durationMs = durationMs; }
          const hasRunningTools = currentMsg.activities.some(
            (activity) => activity.kind === "tool" && activity.status === "running"
          );
          if (!hasRunningTools) {
            removeTrailingThinking();
            currentMsg.activities = [
              ...currentMsg.activities,
              { kind: "thinking", id: `thinking-${assistantId}-${currentMsg.activities.length}` },
            ];
          }
          setStreaming({ ...currentMsg });
        } else if (event.type === "tool_call_args") {
          const tc = findTool(event.tool_call_id);
          if (tc && typeof event.args?.sql === "string") {
            tc.sql = event.args.sql;
            setStreaming({ ...currentMsg });
          }
        } else if (event.type === "chart_component") {
          currentMsg.charts.push(event.chart);
          setStreaming({ ...currentMsg });
        } else if (event.type === "text_delta") {
          removeTrailingThinking();
          currentMsg.content += event.content;
          if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(() => {
              setStreaming({ ...currentMsg });
              rafRef.current = null;
            });
          }
        } else if (event.type === "text_full") {
          removeTrailingThinking();
          currentMsg.content = event.content;
          if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
          setStreaming({ ...currentMsg });
        } else if (event.type === "done") {
          pendingRequestRef.current = null;
          removeTrailingThinking();
          setSessionId(event.session_id);
          onGeneratedDataChange?.(event.stored_data_previews);
          if (event.assistant_message_id) { currentMsg.dbId = event.assistant_message_id; }
          setStreaming(null);
          const next = [...msgsRef.current, { ...currentMsg }];
          msgsRef.current = next;
          setMsgs(next);
          onMessagesChange?.(toDataChatMessages(next), event.session_id);
        } else if (event.type === "error") {
          toast.error("Er is een fout opgetreden. Probeer het opnieuw.");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
          console.error("Stream error:", (event as any).message);
          setStreaming(null);
        }
      },
      () => { setLoading(false); cancelRef.current = null; }
    );

    cancelRef.current = cancel;
  }, [inputMessage, loading, dataSources, sessionId, onMessagesChange, onGeneratedDataChange, administrationCode]);

  const handleCancel = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const pending = pendingRequestRef.current;
    setStreaming(null);
    setLoading(false);

    if (pending) {
      pendingRequestRef.current = null;
      setMsgs(pending.previousMsgs);
      msgsRef.current = pending.previousMsgs;
      setInputMessage(pending.userMsg.content);
      onMessagesChange?.(toDataChatMessages(pending.previousMsgs), pending.sessionId);
    }
  }, [onMessagesChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const allCharts = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
    const charts: any[] = [];
    for (const m of msgs) {
      if (m.role === "assistant") charts.push(...m.charts);
    }
    if (streaming) charts.push(...streaming.charts);
    return charts;
  }, [msgs, streaming]);

  const prevChartCountRef = useRef(0);
  useEffect(() => {
    if (allCharts.length > prevChartCountRef.current) {
      setVizPanelOpen(true);
      setSelectedChartIdx(allCharts.length - 1);
    }
    if (allCharts.length === 0) {
      setVizPanelOpen(false);
    }
    prevChartCountRef.current = allCharts.length;
  }, [allCharts.length]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  const handleChartClick = (chart: any) => {
    const idx = allCharts.indexOf(chart);
    setSelectedChartIdx(idx >= 0 ? idx : 0);
    setVizPanelOpen(true);
  };

  const handleDownload = async (tableName: string, title?: string) => {
    if (!sessionId) {
      toast.error("De dataset is nog niet beschikbaar om te downloaden");
      return;
    }

    await runExport({
      administration_code: administrationCode,
      source: { type: "GENERATED", session_id: sessionId, table_name: tableName },
      filename: title || tableName,
      format: "XLSX",
    });
  };

  const handleVizResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    resizeStateRef.current = { startX: event.clientX, startWidth: vizPanelWidth };
    setIsResizingVizPanel(true);
  };

  return (
    <div ref={containerRef} className={`flex h-full min-h-0 overflow-hidden bg-white ${isResizingVizPanel ? "cursor-col-resize" : ""}`}>
      {/* Chat area */}
      <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">
        <div ref={scrollContainerRef} onScroll={handleScroll} onWheel={markUserScrolling} onTouchMove={markUserScrolling} onKeyDown={markUserScrolling} onPointerDown={holdUserScrolling} className="flex-1 overflow-y-auto min-h-0 px-6 py-6 space-y-5">
          {msgs.length === 0 && !streaming ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <SparklesIcon className="w-10 h-10 text-blue-400" />
              </div>
              <p className="text-gray-400 text-sm">Stel me een vraag over je data — ik zoek het uit.</p>
            </div>
          ) : (
            <>
              {msgs.map(m =>
                m.role === "user"
                  ? <UserBubble key={m.id} content={m.content} />
                  : <AssistantResponse key={m.id} msg={m} onChartClick={handleChartClick} />
              )}
              {streaming && (
                <AssistantResponse msg={streaming} onChartClick={handleChartClick} />
              )}
            </>
          )}
        </div>

        <div className="border-t border-gray-100 px-6 py-4 flex gap-3 items-center">
          <input
            type="text"
            value={inputMessage}
            onChange={e => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={awaitingData ? "Data wordt opgehaald..." : "Stel een vraag over je data..."}
            disabled={loading || dataSources.length === 0}
            className="corner-squircle flex-1 text-sm bg-gray-50 border border-gray-200 rounded-[0.8rem] px-3.5 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white transition-colors disabled:opacity-50"
          />
          {loading ? (
            <button
              onClick={handleCancel}
              title="Annuleer verzoek"
              aria-label="Annuleer verzoek"
              className="p-2.5 text-red-500 hover:text-red-600 transition-colors"
            >
              <span className="block h-3.5 w-3.5 rounded-[2px] bg-current" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!inputMessage.trim()}
              className="p-2.5 text-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Visualization panel */}
      {allCharts.length > 0 && (
        <div
          className={`relative flex-shrink-0 border-l border-gray-200 bg-white ${isResizingVizPanel ? "" : "transition-[width] duration-200 ease-out"}`}
          style={{ width: vizPanelOpen ? `${vizPanelWidth}px` : `${COLLAPSED_VIZ_RAIL_WIDTH}px` }}
        >
          {vizPanelOpen ? (
            <div className="flex h-full min-h-0 flex-col">
              <div
                onMouseDown={handleVizResizeStart}
                className="absolute left-0 top-0 z-10 h-full w-3 -translate-x-1/2 cursor-col-resize"
              >
                <div className="mx-auto h-full w-px bg-transparent transition-colors hover:bg-blue-300" />
              </div>

              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5 flex-shrink-0">
                <button
                  onClick={() => setVizPanelOpen(false)}
                  title="Paneel inklappen"
                  className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <RiArrowRightSLine className="w-4 h-4" />
                </button>
                <div className="min-w-0 text-right">
                  <p className="text-sm font-semibold text-gray-800">
                    Visualisaties{" "}
                    <span className="font-normal text-gray-500">
                      {allCharts.length} grafiek{allCharts.length === 1 ? "" : "en"}
                    </span>
                  </p>
                </div>
              </div>

              {allCharts.length > 1 && (
                <div className="border-b border-gray-100 px-3 py-2 flex flex-col gap-0.5 max-h-[132px] overflow-y-auto flex-shrink-0">
                  {allCharts.map((chart, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedChartIdx(i)}
                      className={`text-left text-xs px-2.5 py-1.5 rounded-lg transition-colors ${selectedChartIdx === i ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      {chart.title || `Grafiek ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}

              {allCharts[selectedChartIdx] && (
                <div className="flex flex-1 min-h-0 flex-col px-4 py-4">
                  {allCharts[selectedChartIdx].title && (
                    <p className="text-sm font-semibold text-gray-800 mb-1">{allCharts[selectedChartIdx].title}</p>
                  )}
                  {allCharts[selectedChartIdx].description && (
                    <p className="text-xs text-gray-500 mb-3">{allCharts[selectedChartIdx].description}</p>
                  )}
                  <div className="min-h-0 flex-1">
                    <ChartRenderer chartSpec={allCharts[selectedChartIdx]} hideTitle fillHeight />
                  </div>
                  {typeof allCharts[selectedChartIdx].stored_data_key === "string" && (
                    <button
                      onClick={() => void handleDownload(allCharts[selectedChartIdx].stored_data_key, allCharts[selectedChartIdx].title)}
                      className="mt-3 flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600"
                    >
                      <RiDownloadLine className="w-3 h-3" /> Download data
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex h-full flex-col items-center py-3">
              <button
                onClick={() => setVizPanelOpen(true)}
                title="Paneel uitklappen"
                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <RiArrowLeftSLine className="w-4 h-4" />
              </button>

              <button
                onClick={() => setVizPanelOpen(true)}
                title="Visualisaties openen"
                className="mt-4 flex flex-1 min-h-0 flex-col items-center justify-center gap-4 text-gray-400 transition-colors hover:text-blue-600"
              >
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-50">
                  <RiBarChart2Line className="h-4 w-4" />
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[9px] font-semibold leading-none text-white">
                    {allCharts.length}
                  </span>
                </span>
                <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] font-semibold uppercase tracking-[0.24em] text-gray-500">
                  Grafieken
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────

const FadeIn = ({ children }: { children: React.ReactNode }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const id = requestAnimationFrame(() => setVisible(true)); return () => cancelAnimationFrame(id); }, []);
  return <div style={{ opacity: visible ? 1 : 0, transition: "opacity 0.25s ease" }}>{children}</div>;
};


const ToolActivityRow = ({ activity }: { activity: ToolActivity }) => {
  const header = (
    <>
      {activity.status === "done" ? (
        <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin flex-shrink-0" />
      )}
      <span>{TOOL_LABELS[activity.name] ?? activity.name}</span>
      {activity.durationMs !== null && <span className="text-gray-300">{(activity.durationMs / 1000).toFixed(1)}s</span>}
    </>
  );

  if (!activity.sql) {
    return <div className="flex items-center gap-2 text-sm text-gray-400">{header}</div>;
  }

  return (
    <details>
      <summary className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer list-none">
        {header}
        <span className="text-gray-400 underline decoration-dotted">Toon query</span>
      </summary>
      <pre className="corner-squircle mt-1 bg-gray-50 border border-gray-200 rounded-[0.85rem] px-2.5 py-2 text-xs font-mono text-gray-700 whitespace-pre-wrap break-words overflow-x-auto">
        {activity.sql}
      </pre>
    </details>
  );
};


const UserBubble = ({ content }: { content: string }) => (
  <div className="flex justify-end">
    <div className="corner-squircle max-w-[65%] bg-blue-500 text-white text-sm rounded-[1.05rem] rounded-br-[0.35rem] px-3 py-2 leading-relaxed">
      {content}
    </div>
  </div>
);

interface AssistantResponseProps {
  msg: AssistantMsg;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  onChartClick?: (chart: any) => void;
}

const AssistantResponse = ({ msg, onChartClick }: AssistantResponseProps) => {
  const [feedback, setFeedback] = useState<boolean | null>(null);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");

  const handleFeedback = (value: boolean) => {
    if (!msg.dbId) return;
    if (value === false) {
      const next = feedback === false ? null : false;
      setFeedback(next);
      setShowFeedbackInput(next === false);
      if (next === null) {
        setFeedbackText("");
        updateMessageFeedback(msg.dbId, false, "").catch(console.error);
      }
      return;
    }
    const next = feedback === true ? null : true;
    setFeedback(next);
    setShowFeedbackInput(false);
    setFeedbackText("");
    if (next !== null) updateMessageFeedback(msg.dbId, next).catch(console.error);
  };

  const handleFeedbackTextSubmit = () => {
    if (!msg.dbId) return;
    updateMessageFeedback(msg.dbId, false, feedbackText).catch(console.error);
    setShowFeedbackInput(false);
  };

  return (
  <div className="flex justify-start">
    <div className="max-w-2xl w-full flex flex-col gap-3">
      {msg.activities.length > 0 && (
        <div className="flex flex-col gap-1">
          {msg.activities.map((activity) => (
            <FadeIn key={activity.id}>
              {activity.kind === "thinking" ? (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin flex-shrink-0" />
                  <span>Denken...</span>
                </div>
              ) : (
                <ToolActivityRow activity={activity} />
              )}
            </FadeIn>
          ))}
        </div>
      )}

      {msg.content && (
        <div className="text-sm text-gray-800 leading-relaxed">
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              h1: ({ children }) => <h1 className="text-base font-semibold mt-3 mb-1">{children}</h1>,
              h2: ({ children }) => <h2 className="text-sm font-semibold mt-3 mb-1">{children}</h2>,
              h3: ({ children }) => <h3 className="text-sm font-medium mt-2 mb-1">{children}</h3>,
              ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
              li: ({ children }) => <li>{children}</li>,
              code: ({ children, className }) =>
                className ? (
                  <pre className="corner-squircle bg-gray-50 border border-gray-200 rounded-[0.85rem] px-2.5 py-2 text-xs font-mono overflow-x-auto mb-2"><code>{children}</code></pre>
                ) : (
                  <code className="bg-gray-100 text-gray-700 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
                ),
              pre: ({ children }) => <>{children}</>,
              strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
              a: ({ href, children }) => <a href={href} className="text-blue-500 hover:underline" target="_blank" rel="noreferrer">{children}</a>,
              blockquote: ({ children }) => <blockquote className="border-l-2 border-gray-200 pl-3 text-gray-500 my-2">{children}</blockquote>,
              table: ({ children }) => <div className="overflow-x-auto mb-2"><table className="text-xs border-collapse w-full">{children}</table></div>,
              th: ({ children }) => <th className="border border-gray-200 px-2 py-1 bg-gray-50 font-medium text-left">{children}</th>,
              td: ({ children }) => <td className="border border-gray-200 px-2 py-1">{children}</td>,
            }}
          >
            {msg.content}
          </ReactMarkdown>
        </div>
      )}

      {msg.charts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {msg.charts.map((chart, i) => (
            <FadeIn key={i}>
              <button
                onClick={() => onChartClick?.(chart)}
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 hover:border-blue-200 rounded-full px-3 py-1.5 transition-colors"
              >
                <RiBarChart2Line className="w-3.5 h-3.5 flex-shrink-0" />
                {chart.title || `Grafiek ${i + 1}`}
              </button>
            </FadeIn>
          ))}
        </div>
      )}

      {msg.dbId && (
        <div className="flex flex-col gap-1.5 mt-0.5">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => handleFeedback(true)}
              title="Nuttig"
              className={`p-1 rounded transition-colors ${feedback === true ? "text-green-500" : "text-gray-300 hover:text-gray-400"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill={feedback === true ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>
              </svg>
            </button>
            <button
              onClick={() => handleFeedback(false)}
              title="Niet nuttig"
              className={`p-1 rounded transition-colors ${feedback === false ? "text-red-400" : "text-gray-300 hover:text-gray-400"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill={feedback === false ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/>
              </svg>
            </button>
          </div>
          {showFeedbackInput && (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                type="text"
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleFeedbackTextSubmit(); if (e.key === "Escape") { setShowFeedbackInput(false); setFeedbackText(""); } }}
                placeholder="Wat ging er mis?"
                className="text-xs bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-red-300 w-48 transition-colors"
              />
              <button
                onClick={handleFeedbackTextSubmit}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors px-1"
              >
                Verstuur
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
  );
};

export default DataChat;
